/* eslint-disable multiline-ternary */
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons"
import { Box, Button, Flex, Select, Text } from "@chakra-ui/react"
import { Colors } from "../../../theme/colors"

interface FMPPaginationProps {
  data: any
  numberOfRowsPerPage: number
  activePage: number
  setActivePage: (page: number) => void
  handleNumberOfRowsPerPage: (rows: number) => void
}

export const FMPPagination = ({
  data,
  numberOfRowsPerPage,
  activePage,
  setActivePage,
  handleNumberOfRowsPerPage
}: FMPPaginationProps): React.ReactNode => {
  const totalRecords = data?.columns?.length || 0
  const totalPages = Math.ceil(totalRecords / numberOfRowsPerPage)
  const startRecord = activePage === 0 ? 1 : activePage * numberOfRowsPerPage + 1
  const endRecord = Math.min((activePage + 1) * numberOfRowsPerPage, totalRecords)
  const pageArray = [10, 20, 30, 50, 100]

  const commonButtonStyles = {
    size: "sm",
    variant: "ghost",
    borderRadius: "4px",
    width: "32px",
    height: "32px",
    transition: "all 0.15s ease-in-out",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bg: "gray.50",
    _hover: { bg: "gray.100" }
  }

  const renderPageButtons = (): React.ReactNode[] => {
    const FIRST_PAGE = 0
    const LAST_PAGE = totalPages - 1
    const MIDDLE_BUTTONS_COUNT = 5 // Number of buttons to show in the middle

    // Calculate the range of buttons to show
    let startPage = Math.max(1, activePage - Math.floor(MIDDLE_BUTTONS_COUNT / 2))
    let endPage = Math.min(startPage + MIDDLE_BUTTONS_COUNT - 1, LAST_PAGE)

    // Adjust startPage if we're near the end
    if (endPage === LAST_PAGE) {
      startPage = Math.max(1, LAST_PAGE - MIDDLE_BUTTONS_COUNT + 1)
    }

    // If we're near the start, adjust to show first MIDDLE_BUTTONS_COUNT pages
    if (activePage <= 1) {
      startPage = 1
      endPage = Math.min(MIDDLE_BUTTONS_COUNT, LAST_PAGE)
    }

    const firstPageButton = (
      <Button
        key="first"
        {...commonButtonStyles}
        _hover={{ bg: activePage === FIRST_PAGE ? "primary" : "gray.100", color: activePage === FIRST_PAGE ? "white" : "gray.900" }}
        onClick={() => {
          setActivePage(FIRST_PAGE)
        }}
        bg={activePage === FIRST_PAGE ? "primary" : "gray.50"}
        color={activePage === FIRST_PAGE ? "white" : "gray.900"}
        fontWeight={activePage === FIRST_PAGE ? "medium" : "normal"}
      >
        1
      </Button>
    )

    const prevButton = (
      <Button
        key="prev"
        {...commonButtonStyles}
        onClick={() => {
          setActivePage(Math.max(FIRST_PAGE, activePage - MIDDLE_BUTTONS_COUNT))
        }}
        isDisabled={activePage === FIRST_PAGE}
        opacity={activePage === FIRST_PAGE ? 0.5 : 1}
      >
        <ChevronLeftIcon fontSize={"24px"} color="gray.600" />
      </Button>
    )

    const middleButtons = Array.from({ length: endPage - startPage + 1 }, (_, i) => {
      const pageNum = startPage + i
      if (pageNum === FIRST_PAGE || pageNum === LAST_PAGE) return null
      return (
        <Button
          key={pageNum}
          {...commonButtonStyles}
          _hover={{ bg: activePage === pageNum ? "primary" : "gray.100", color: activePage === pageNum ? "white" : "gray.900" }}
          onClick={() => {
            setActivePage(pageNum)
          }}
          transition="none"
          bg={activePage === pageNum ? "primary" : "gray.50"}
          color={activePage === pageNum ? "white" : "gray.900"}
          fontWeight={activePage === pageNum ? "medium" : "normal"}
        >
          {pageNum + 1}
        </Button>
      )
    }).filter(Boolean)

    const nextButton = (
      <Button
        key="next"
        {...commonButtonStyles}
        onClick={() => {
          const nextPage = Math.min(LAST_PAGE, activePage + MIDDLE_BUTTONS_COUNT)
          setActivePage(nextPage)
        }}
        isDisabled={activePage === LAST_PAGE}
        opacity={activePage === LAST_PAGE ? 0.5 : 1}
      >
        <ChevronRightIcon fontSize={"24px"} color="gray.600" />
      </Button>
    )

    const lastPageButton =
      LAST_PAGE > FIRST_PAGE ? (
        <Button
          key="last"
          {...commonButtonStyles}
          _hover={{ bg: activePage === LAST_PAGE ? "primary" : "gray.100", color: activePage === LAST_PAGE ? "white" : "gray.900" }}
          onClick={() => {
            setActivePage(LAST_PAGE)
          }}
          bg={activePage === LAST_PAGE ? "primary" : "gray.50"}
          color={activePage === LAST_PAGE ? "white" : "gray.900"}
          fontWeight={activePage === LAST_PAGE ? "medium" : "normal"}
        >
          {LAST_PAGE + 1}
        </Button>
      ) : null

    return [firstPageButton, prevButton, ...middleButtons, nextButton, lastPageButton].filter(Boolean)
  }

  return (
    <Flex justifyContent="flex-end" borderColor={Colors.Secondary.c2_300} pb="2" bg="white" w="100%">
      <Flex gap="8px" alignItems="flex-end">
        <Flex flexDirection="column" w="170px" gap="4px">
          <Text fontSize="11px" color="gray.500" w="100%">
            Showing{" "}
            <Text as="span" fontWeight={"bold"}>
              {startRecord}
            </Text>{" "}
            to{" "}
            <Text as="span" fontWeight={"bold"}>
              {endRecord}
            </Text>
            {""} of <Text as="span">{totalRecords}</Text> records
          </Text>
          <Select
            size="sm"
            width="100%"
            fontSize="12px"
            borderRadius="4px"
            color={"gray.500"}
            value={numberOfRowsPerPage}
            onChange={e => {
              handleNumberOfRowsPerPage(Number(e.target.value))
              setActivePage(0) // Reset to first page when changing rows per page
            }}
          >
            {pageArray.map(value => (
              <option key={value} value={value}>
                {value} records per page
              </option>
            ))}
          </Select>
        </Flex>

        {data?.columns?.length > 0 && (
          <Box>
            <Box display="flex" gap="4px">
              {renderPageButtons()}
            </Box>
          </Box>
        )}
      </Flex>
    </Flex>
  )
}
